<script lang="ts">
  import { Alert } from "@getprovi/craft-svelte";

  import { email } from "@shared/svelte/Validators/Email";
  import { hasOnlyLowercaseAlphanumeric } from "@shared/svelte/Validators/OhlqApiKey";

  import { InputText } from "@getprovi/craft-svelte";

  import { EventTracking } from "@shared/services/event_tracking/events";
  import { RetailerUserAuthenticationEventEntity } from "@shared/services/event_tracking/entities/retailer_user_authentication_event_entity";
  import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  export let apiEmail: string;
  export let apiKey: string;
  export let invalid: boolean;
  export let primaryBtnProps;
  export let invalidState: boolean = false;
  export let authStatus: string = "";
  export let validationError: string = "";

  type InputState = "neutral" | "success" | "critical" | "disabled";

  function validateApiKey(e: InputEvent & { target: HTMLInputElement }) {
    invalidState = false;
    if (!e.target.value) {
      fields.apiKey.errorText = "API Key is required.";
      fields.apiKey.state = "critical";

      let entity = new RetailerUserAuthenticationEventEntity({
        distributorId: OHLQ_DISTRIBUTOR_ID,
        step: "apiKey",
        field: "apiKey",
        error: "missing",
        authenticationStatus: authStatus,
      });
      EventTracking.trackClick("retailer_user_authentication_flow_error", [entity]);

      return;
    }

    apiKeyBlank = false;

    const validator = hasOnlyLowercaseAlphanumeric();

    if (!validator(e.target.value).valid) {
      fields.apiKey.errorText = "API Key must be 20 lowercase alphanumeric characters.";
      fields.apiKey.state = "critical";
      let entity = new RetailerUserAuthenticationEventEntity({
        distributorId: OHLQ_DISTRIBUTOR_ID,
        step: "apiKey",
        field: "apiKey",
        error: "invalid",
        authenticationStatus: authStatus,
      });
      EventTracking.trackClick("retailer_user_authentication_flow_error", [entity]);
    } else {
      fields.apiKey.errorText = "";
      fields.apiKey.state = "neutral";
    }
  }

  async function validateEmail(e: InputEvent & { target: HTMLInputElement }) {
    invalidState = false;
    if (!e.target.value) {
      fields.email.errorText = "Email is required.";
      fields.email.state = "critical";

      let entity = new RetailerUserAuthenticationEventEntity({
        distributorId: OHLQ_DISTRIBUTOR_ID,
        step: "apiKey",
        field: "email",
        error: "missing",
        authenticationStatus: authStatus,
      });
      EventTracking.trackClick("retailer_user_authentication_flow_error", [entity]);

      return;
    }

    emailBlank = false;
    const validation = await email(e.target.value);

    if (!validation.valid) {
      fields.email.errorText = "Incorrect email format.";
      fields.email.state = "critical";
      let entity = new RetailerUserAuthenticationEventEntity({
        distributorId: OHLQ_DISTRIBUTOR_ID,
        step: "apiKey",
        field: "email",
        error: "invalid",
        authenticationStatus: authStatus,
      });
      EventTracking.trackClick("retailer_user_authentication_flow_error", [entity]);
    } else {
      fields.email.errorText = "";
      fields.email.state = "neutral";
    }
  }

  const fields: { [key: string]: { errorText: string; state: InputState; value: string } } = {
    apiKey: {
      errorText: "",
      state: "neutral",
      value: "",
    },
    email: {
      errorText: "",
      state: "neutral",
      value: "",
    },
  };

  let emailBlank: boolean = true;
  let apiKeyBlank: boolean = true;

  $: {
    invalid =
      Object.values(fields).some(field => field.state === "critical") || emailBlank || apiKeyBlank;
    primaryBtnProps.disabled = invalid;
  }
  $: apiKey = fields.apiKey.value;
  $: apiEmail = fields.email.value;

  function focusEvent(field) {
    let entity = new RetailerUserAuthenticationEventEntity({
      distributorId: OHLQ_DISTRIBUTOR_ID,
      step: "apiKey",
      field: field,
      authenticationStatus: authStatus,
    });
    EventTracking.trackClick("retailer_user_authentication_focus_field", [entity]);
  }
</script>

<div class="flex flex-column items-center fizz-stack-32">
  <img
    src="https://portal.ohlq.com/Portals/0/OHLQ_2_color_horizontal_TM.jpg?ver=2019-07-29-112714-850"
    alt="OHLQ"
    style="max-width: 147px;"
  />
  <h2 class="flex-1 text-center">Enter Your API Key</h2>
</div>
<p class="fizz-text-subdued fizz-stack-16 fizz-text-large text-center">
  This can be found in the 'Profile' section of your WSC account.
</p>
{#if invalidState}
  <div class="fizz-inset-horizontal-16">
    <Alert title="We can’t verify your Wholesale Cloud Account." variant="critical">
      {#if validationError}
        {validationError}
      {:else}
        Check your API Key and email and try again.
      {/if}
    </Alert>
  </div>
{/if}
<div class="fizz-inset-vertical-16 fizz-stack-32">
  <div class="fizz-stack-16">
    <InputText
      bind:value={fields.apiKey.value}
      on:change={validateApiKey}
      on:focus={() => focusEvent("apiKey")}
      class=""
      errorText={fields.apiKey.errorText}
      hiddenLabel={false}
      id="api_key"
      label="API Key"
      labelClasses=""
      placeholder=""
      size="default"
      state={fields.apiKey.state}
    />
  </div>
  <div class="fizz-stack-16">
    <InputText
      bind:value={fields.email.value}
      on:change={validateEmail}
      on:focus={() => focusEvent("email")}
      class=""
      errorText={fields.email.errorText}
      hiddenLabel={false}
      id="email"
      label="Wholesale Cloud Account Email"
      labelClasses=""
      placeholder=""
      size="default"
      state={fields.email.state}
    />
  </div>
</div>
<div class="fizz-text-subdued">
  <p class="text-center">
    <span class="fizz-text-strong">Need help?</span> Contact OHLQ Liquor Enterprise Service Center
    at
    <a href="tel:+18778120013">877-812-0013</a>
  </p>
</div>
